/**
 * `Stamp` — a rubber-stamped mark in real label print. `docs/design-direction.md`
 * §6.8.
 *
 * The ink, the 1px box, the +0.18em tracking, the grain dropout mask and the −7°
 * tilt are the global `.stamp` recipe in `styles/texture.css`. What this component
 * owns is the copy pool, the crop and the two states the contract gives a stamp.
 *
 * **The copy pool is frozen.** {@link STAMP_COPY} is the whole vocabulary: real
 * pressing-plant and sound-system print, never invented patois. A Russian-language
 * app faking Jamaican English is the exact failure mode this direction exists to
 * avoid (§11), so a stamp cannot be handed arbitrary text — in development that
 * throws, and in production the stamp renders nothing rather than print a phrase no
 * plant ever stamped.
 *
 * **A stamp is always cropped by a screen edge** (§6.8): half a stamp reads as
 * confidence, a centred whole stamp reads as a sticker. `edge` positions it against
 * the nearest positioned, clipping ancestor — the screen shell — at the documented
 * z-50 layer for "stamps that crop the screen edge" (§5.7). A stamp with no `edge`
 * sits in flow, which is the one composition the contract also asks for: the recent
 * queries above the search results, alternating −2.5°/−4.5° so the row looks
 * hand-stamped (§8.3).
 *
 * **Interactive form.** `PULL UP!` is a stamp *and* a control: under reduced motion
 * the pull-up ritual degrades to a tap on it, which restarts the track — the ritual
 * survives, the inertia does not, and the pull-up may never ship without this
 * fallback (§7.3, §12.23). Given `onPress`, the stamp becomes a real `<button>` with
 * its own 44px target and a Russian accessible name; the English print stays as the
 * visible copy, exactly as it would on the record.
 *
 * Blend-mode budget (§6.13): `.stamp` carries `mix-blend-mode: screen`, and a screen
 * may show at most three blended elements — the grain layer, the misregistration
 * ghost, and **one** of {stamp, marker, art duotone}. Two stamps on one screen is
 * over budget; the reviewer counts them.
 */

import type { CSSProperties, JSX, ReactNode } from 'react';

import styles from './primitives.module.css';
import { cx } from './Rule';

/**
 * The complete copy pool of §6.8 — real label print. Adding to it is a design
 * decision, not an implementation one: it is the difference between quoting a
 * pressing plant and inventing patois (§11).
 */
export const STAMP_COPY = Object.freeze([
  'DUBPLATE SPECIAL',
  'NOT FOR SALE',
  'PRE-RELEASE',
  'VERSION',
  'SIDE A',
  'SIDE B',
  'PULL UP!',
  'NO SIGNAL',
] as const);

/** One of the eight licensed stamp phrases. */
export type StampCopy = (typeof STAMP_COPY)[number];

/**
 * The closed set of stamp angles the contract uses: −7° for print stamps (§6.8), −4°
 * for `NO SIGNAL` over a dimmed crate (§8.10), and the alternating −2.5°/−4.5° of the
 * search screen's recent queries (§8.3).
 */
export type StampTilt = -7 | -4.5 | -4 | -2.5;

/** Which screen edge crops the stamp. Flow position (no crop) is `undefined`. */
export type StampEdge = 'left' | 'right' | 'top';

export interface StampProps {
  /** Copy, from the frozen pool. Anything else throws in development. */
  readonly copy: StampCopy;
  /** Rotation. Default −7°, the print-stamp angle of §6.8. */
  readonly tilt?: StampTilt;
  /** Screen edge to bleed past. Omit to sit in flow (§8.3's search stamps). */
  readonly edge?: StampEdge | undefined;
  /**
   * Makes the stamp a control. Required for the reduced-motion pull-up (§7.3); the
   * accessible name comes from `pressLabel`, because the visible copy is English
   * label print and the UI language is Russian (§11).
   */
  readonly onPress?: (() => void) | undefined;
  /** Russian accessible name for the interactive form. Required with `onPress`. */
  readonly pressLabel?: string | undefined;
  /**
   * The one-shot slam-in of §7.3 — scale 1.25 → 1, no easing. Only `PULL UP!` uses
   * it, and only on the release of a real pull-up.
   */
  readonly slam?: boolean;
  readonly className?: string | undefined;
}

function isLicensedCopy(copy: string): copy is StampCopy {
  return (STAMP_COPY as readonly string[]).includes(copy);
}

/**
 * A stamp.
 *
 * @throws Error in development when `copy` is outside {@link STAMP_COPY}, or when
 * `onPress` is given without a Russian `pressLabel`. In production both fail soft:
 * unlicensed copy renders nothing (an invented stamp is worse than a missing one),
 * and a nameless control renders as a non-interactive mark rather than as a button
 * no screen reader can announce.
 */
export function Stamp({
  copy,
  tilt = -7,
  edge,
  onPress,
  pressLabel,
  slam = false,
  className,
}: StampProps): JSX.Element | null {
  const licensed = isLicensedCopy(copy);
  const named = typeof pressLabel === 'string' && pressLabel.trim() !== '';

  if (import.meta.env.DEV) {
    if (!licensed) {
      throw new Error(
        `Stamp: copy ${JSON.stringify(copy)} is outside the frozen pool of ` +
          `design-direction.md §6.8. Licensed copy: ${STAMP_COPY.join(' · ')}.`,
      );
    }
    if (onPress !== undefined && !named) {
      throw new Error(
        'Stamp: an interactive stamp needs a Russian `pressLabel` — the visible copy ' +
          'is English label print and cannot be its accessible name (§11).',
      );
    }
  }

  if (!licensed) return null;

  /* A custom property is not part of `CSSProperties`; the value is a plain string. */
  const style = { '--stamp-tilt': `${tilt}deg` } as CSSProperties;

  const classes = cx(
    'stamp',
    't-mono',
    't-11',
    styles.stamp,
    slam && styles.slam,
    onPress !== undefined && named && styles.press,
    onPress !== undefined && named && styles.hit,
    className,
  );

  const body: ReactNode = copy;

  if (onPress !== undefined && named) {
    return (
      <button
        type="button"
        className={classes}
        style={style}
        data-edge={edge}
        data-copy={copy}
        aria-label={pressLabel}
        onClick={onPress}
      >
        {body}
      </button>
    );
  }

  return (
    <span className={classes} style={style} data-edge={edge} data-copy={copy}>
      {body}
    </span>
  );
}
